import React from 'react';
import {
  Box,
  Typography,
  Paper,
  Chip,
  Divider,
  useTheme,
} from '@mui/material';
import {
  CheckCircle,
  Cancel,
  Remove,
} from '@mui/icons-material';

interface ComparisonPIGraphProps {
  candidate: {
    pi_factor_a: number;
    pi_factor_b: number;
    pi_factor_c: number;
    pi_factor_d: number;
    first_name: string;
    last_name: string;
  };
  idealRanges: {
    a?: [number, number];
    b?: [number, number];
    c?: [number, number];
    d?: [number, number];
  } | null;
  relationshipRequirements?: {
    [key: string]: boolean | undefined;
  };
  matchBreakdown?: any;
}

const ComparisonPIGraph: React.FC<ComparisonPIGraphProps> = ({
  candidate,
  idealRanges,
  relationshipRequirements,
  matchBreakdown,
}) => {
  const theme = useTheme();

  // Convert 0-100 scale to sigma scale (-3 to +3)
  const scaleToSigma = (value: number) => (value - 50) / 16.67;

  // Round sigma to nearest 0.25 for ideal ranges
  const roundToQuarter = (sigma: number) => Math.round(sigma * 4) / 4;

  // Factor labels and descriptions
  const factors = [
    {
      key: 'a',
      label: 'A - Dominance',
      description: 'Drive for results and control',
      candidateValue: candidate.pi_factor_a,
      idealRange: idealRanges?.a,
    },
    {
      key: 'b',
      label: 'B - Extraversion',
      description: 'Social interaction and influence',
      candidateValue: candidate.pi_factor_b,
      idealRange: idealRanges?.b,
    },
    {
      key: 'c',
      label: 'C - Patience',
      description: 'Stability and consistency',
      candidateValue: candidate.pi_factor_c,
      idealRange: idealRanges?.c,
    },
    {
      key: 'd',
      label: 'D - Formality',
      description: 'Structure and conformity',
      candidateValue: candidate.pi_factor_d,
      idealRange: idealRanges?.d,
    },
  ].filter(f => f.idealRange !== undefined);  // Only show factors with defined ideal ranges

  // Determine match status for each factor
  const getFactorMatch = (candidateValue: number, idealRange: [number, number]) => {
    const [min, max] = idealRange;
    if (candidateValue >= min && candidateValue <= max) {
      return { status: 'perfect', color: theme.palette.success.main, icon: CheckCircle };
    }
    
    // Check if close (within 25% tolerance)
    const range = max - min;
    const tolerance = range * 0.25;
    if (candidateValue >= (min - tolerance) && candidateValue <= (max + tolerance)) {
      return { status: 'close', color: theme.palette.warning.main, icon: Remove };
    }
    
    return { status: 'poor', color: theme.palette.error.main, icon: Cancel };
  };

  // Create visual graph for a single factor
  const FactorGraph: React.FC<{
    factor: typeof factors[0];
    height?: number;
  }> = ({ factor, height = 80 }) => {
    if (!factor.idealRange) return null;  // Skip if no ideal range defined
    
    const match = getFactorMatch(factor.candidateValue, factor.idealRange);
    const candidateSigma = scaleToSigma(factor.candidateValue);
    const idealMinSigma = scaleToSigma(factor.idealRange[0]);
    const idealMaxSigma = scaleToSigma(factor.idealRange[1]);
    
    const graphWidth = 400;
    const sigmaToPosition = (sigma: number) => ((sigma + 3) / 6) * graphWidth;
    
    const candidatePosition = sigmaToPosition(candidateSigma);
    const idealMinPosition = sigmaToPosition(idealMinSigma);
    const idealMaxPosition = sigmaToPosition(idealMaxSigma);

    return (
      <Box sx={{ mb: 3 }}>
        <Box display="flex" alignItems="center" gap={1} mb={1}>
          <match.icon sx={{ color: match.color, fontSize: 20 }} />
          <Typography variant="h6" fontWeight="medium">
            {factor.label}
          </Typography>
          <Chip
            label={match.status === 'perfect' ? 'Perfect Match' : match.status === 'close' ? 'Close Match' : 'Poor Match'}
            size="small"
            sx={{
              backgroundColor: match.color + '20',
              color: match.color,
              fontWeight: 'medium',
            }}
          />
        </Box>
        
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {factor.description}
        </Typography>

        {/* Graph Container */}
        <Box
          sx={{
            position: 'relative',
            width: graphWidth,
            height,
            backgroundColor: 'grey.100',
            borderRadius: 1,
            border: '1px solid',
            borderColor: 'grey.300',
          }}
        >
          {/* Sigma scale labels (no lines) */}
          {[-3, -2, -1, 0, 1, 2, 3].map((sigma) => (
            <Box
              key={sigma}
              sx={{
                position: 'absolute',
                left: sigmaToPosition(sigma),
                bottom: -20,
                transform: 'translateX(-50%)',
                fontSize: '0.7rem',
                color: 'text.secondary',
                fontWeight: sigma === 0 ? 'bold' : 'normal',
              }}
            >
              {sigma}σ
            </Box>
          ))}

          {/* Ideal range highlight */}
          {factor.idealRange && (
            <Box
              sx={{
                position: 'absolute',
                left: idealMinPosition,
                width: idealMaxPosition - idealMinPosition,
                top: 0,
                bottom: 0,
                backgroundColor: '#ff980030',  // Light orange with transparency
                border: `2px solid #ff9800`,  // Orange border
                borderRadius: 1,
              }}
            />
          )}

          {/* Ideal range labels */}
          {factor.idealRange && (
            <Box
              sx={{
                position: 'absolute',
                left: (idealMinPosition + idealMaxPosition) / 2 - 30,
                top: height / 2 - 10,
                backgroundColor: 'white',
                padding: '2px 6px',
                borderRadius: 1,
                fontSize: '0.7rem',
                fontWeight: 'medium',
                color: '#e65100',  // Dark orange text
              }}
            >
              Ideal Range
            </Box>
          )}

          {/* Candidate position */}
          <Box
            sx={{
              position: 'absolute',
              left: candidatePosition - 8,
              top: 5,
              width: 16,
              height: height - 10,
              backgroundColor: match.color,
              borderRadius: 2,
              border: '2px solid white',
              boxShadow: 2,
            }}
          />

          {/* Candidate label */}
          <Box
            sx={{
              position: 'absolute',
              left: candidatePosition - 25,
              top: -25,
              backgroundColor: match.color,
              color: 'white',
              padding: '2px 8px',
              borderRadius: 1,
              fontSize: '0.75rem',
              fontWeight: 'medium',
              whiteSpace: 'nowrap',
            }}
          >
            {candidate.first_name} ({candidateSigma.toFixed(2)}σ)
          </Box>
        </Box>

        {/* Value comparison */}
        <Box display="flex" justifyContent="space-between" mt={2}>
          <Typography variant="body2" data-testid={`pi-factor-${factor.key}`}>
            <strong>Candidate:</strong> {candidateSigma.toFixed(2)}σ (PI Score: {factor.candidateValue})
          </Typography>
          <Typography variant="body2">
            <strong>Ideal Range:</strong> {roundToQuarter(idealMinSigma)}σ to {roundToQuarter(idealMaxSigma)}σ
          </Typography>
        </Box>
      </Box>
    );
  };

  // Relationship requirements analysis
  const getRelationshipAnalysis = () => {
    if (!relationshipRequirements || !matchBreakdown) return [];

    const relationships: Array<{
      label: string;
      required: boolean;
      met: boolean;
      candidateComparison: string;
    }> = [];

    const compScores = matchBreakdown.comparison_scores || {};
    const candidateFactors = {
      a: candidate.pi_factor_a,
      b: candidate.pi_factor_b,
      c: candidate.pi_factor_c,
      d: candidate.pi_factor_d,
    };

    // All possible relationships
    const relationshipChecks = [
      { req: 'requires_a_greater_than_b', score: 'a_greater_than_b', label: 'A > B', compare: () => candidateFactors.a > candidateFactors.b },
      { req: 'requires_a_greater_than_c', score: 'a_greater_than_c', label: 'A > C', compare: () => candidateFactors.a > candidateFactors.c },
      { req: 'requires_a_greater_than_d', score: 'a_greater_than_d', label: 'A > D', compare: () => candidateFactors.a > candidateFactors.d },
      { req: 'requires_b_greater_than_a', score: 'b_greater_than_a', label: 'B > A', compare: () => candidateFactors.b > candidateFactors.a },
      { req: 'requires_b_greater_than_c', score: 'b_greater_than_c', label: 'B > C', compare: () => candidateFactors.b > candidateFactors.c },
      { req: 'requires_b_greater_than_d', score: 'b_greater_than_d', label: 'B > D', compare: () => candidateFactors.b > candidateFactors.d },
      { req: 'requires_c_greater_than_a', score: 'c_greater_than_a', label: 'C > A', compare: () => candidateFactors.c > candidateFactors.a },
      { req: 'requires_c_greater_than_b', score: 'c_greater_than_b', label: 'C > B', compare: () => candidateFactors.c > candidateFactors.b },
      { req: 'requires_c_greater_than_d', score: 'c_greater_than_d', label: 'C > D', compare: () => candidateFactors.c > candidateFactors.d },
      { req: 'requires_d_greater_than_a', score: 'd_greater_than_a', label: 'D > A', compare: () => candidateFactors.d > candidateFactors.a },
      { req: 'requires_d_greater_than_b', score: 'd_greater_than_b', label: 'D > B', compare: () => candidateFactors.d > candidateFactors.b },
      { req: 'requires_d_greater_than_c', score: 'd_greater_than_c', label: 'D > C', compare: () => candidateFactors.d > candidateFactors.c },
    ];

    relationshipChecks.forEach(check => {
      if (relationshipRequirements[check.req]) {
        const met = compScores[check.score] === 1;
        const actuallyMet = check.compare();
        relationships.push({
          label: check.label,
          required: true,
          met,
          candidateComparison: actuallyMet ? `✓ ${check.label}` : `✗ ${check.label}`,
        });
      }
    });

    return relationships;
  };

  const relationshipAnalysis = getRelationshipAnalysis();

  return (
    <Paper sx={{ p: 3 }}>
      <Typography variant="h5" gutterBottom>
        PI Profile Comparison: {candidate.first_name} {candidate.last_name}
      </Typography>
      
      <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
        Detailed comparison between candidate's behavioral profile and position requirements
      </Typography>
      
      <Box sx={{ mb: 3, p: 2, backgroundColor: 'grey.50', borderRadius: 1 }}>
        <Typography variant="body2" sx={{ mb: 1 }}>
          <strong>Graph Legend:</strong>
        </Typography>
        <Typography variant="body2" component="div">
          • <strong>Light Grey Background:</strong> Full sigma scale (-3σ to +3σ) showing all possible behavioral ranges<br/>
          • <strong>Green Highlighted Area:</strong> Ideal range for this position - where candidates should score for optimal performance<br/>
          • <strong>Colored Bar:</strong> Candidate's actual score position (green = in range, orange = close, red = outside range)
        </Typography>
      </Box>

      <Divider sx={{ mb: 3 }} />

      {/* Factor-by-factor comparison */}
      <Typography variant="h6" gutterBottom>
        Factor Analysis
      </Typography>
      
      {factors.length > 0 ? (
        factors.map((factor) => (
          <FactorGraph key={factor.key} factor={factor} />
        ))
      ) : (
        <Box sx={{ p: 3, textAlign: 'center', color: 'text.secondary' }}>
          <Typography variant="body1">
            No ideal PI ranges have been defined for this position.
          </Typography>
          <Typography variant="body2" sx={{ mt: 1 }}>
            To see a comparison, set the ideal ranges in the job requirements.
          </Typography>
        </Box>
      )}

      {/* Relationship requirements */}
      {relationshipAnalysis.length > 0 && (
        <>
          <Divider sx={{ my: 3 }} />
          <Typography variant="h6" gutterBottom>
            Relationship Requirements
          </Typography>
          
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(3, 1fr)' },
              gap: 2,
            }}
          >
            {relationshipAnalysis.map((rel, idx) => (
              <Box key={idx}>
                <Paper
                  variant="outlined"
                  sx={{
                    p: 2,
                    backgroundColor: rel.met ? 'success.light' : 'error.light',
                    borderColor: rel.met ? 'success.main' : 'error.main',
                  }}
                >
                  <Box display="flex" alignItems="center" gap={1}>
                    {rel.met ? (
                      <CheckCircle sx={{ color: 'success.dark' }} />
                    ) : (
                      <Cancel sx={{ color: 'error.dark' }} />
                    )}
                    <Typography variant="subtitle2" fontWeight="medium">
                      {rel.label}
                    </Typography>
                  </Box>
                  <Typography variant="body2" sx={{ mt: 1 }}>
                    {rel.candidateComparison}
                  </Typography>
                </Paper>
              </Box>
            ))}
          </Box>
        </>
      )}
    </Paper>
  );
};

export default ComparisonPIGraph;